import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IconButton, Menu, MenuItem, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import DeleteIcon from "mdi-react/DeleteIcon";
import { DownloadInProgressIcon, DownloadCompleteIcon } from "helpers/icon-finder";
import { getSignedInOrgUnit } from "app/auth/store/reducers/user.reducer";
import PulsingIconButton from "components/items/pulsing-icon-button";
import { getExportJobs,
  showExportCompleteIndicator,
  getExportCompleteJobId,
  isReportExportLoading,
  getPulseState, isPendingExportExists } from "../reducers/reportExport.reducers";
import { getExportJobList,
  downloadExportedReport,
  removeExportCompleteIndicator,
  clearExportedReport,
  clearExportCompleteJob,
  removePendingExport } from "../actions/reportExport.actions";
import { getJobStatusLabel,
  isJobStatusInProgress,
  isJobStatusComplete,
  isJobStatusCancelled,
  isJobStatusFailedOrCancelled } from "../constants/jobStatusTypes";

const useStyles = makeStyles(theme => ({
  root: {
    position: "relative",
  },
  menuItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(0.5, 1),
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  menuItemContent: {
    flexGrow: 1,
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(2),
    overflow: "hidden",
  },
  fileName: {
    fontWeight: 500,
    color: theme.palette.text.primary,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  fileNameFailed: {
    fontWeight: 500,
    color: theme.palette.error.main,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  statusLabel: {
    color: theme.palette.text.secondary,
  },
  statusLabelFailed: {
    color: theme.palette.error.main,
  },
  actionButtons: {
    flexShrink: 0,
  },
  actionButtonsFailed: {
    flexShrink: 0,
    "& .MuiIconButton-root": {
      color: theme.palette.error.main,
    },
    "& .MuiIconButton-root.download-button": {
      opacity: 0.38, // Material-UI's default disabled opacity
    },
  },
  deleteButton: {
    marginLeft: theme.spacing(1),
  },
  deleteButtonFailed: {
    marginLeft: theme.spacing(1),
    color: theme.palette.error.main,
  },
}));

const BUTTON_ID = "export-dropdown-button";

const ReportExportDropDown = () => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentIcon, setCurrentIcon] = useState(<DownloadCompleteIcon />);
  const dispatch = useDispatch();
  const isLoading = useSelector(isReportExportLoading);
  const signedInOrgUnit = useSelector(getSignedInOrgUnit);
  const exportJobs = useSelector(getExportJobs);
  const exportCompleteJobId = useSelector(getExportCompleteJobId);
  const wasExportInitiatedInCurrentSession = useSelector(state => (
    exportCompleteJobId
      ? isPendingExportExists(state, exportCompleteJobId)
      : false));
  const showIndicator = useSelector(showExportCompleteIndicator);
  const showPulseEffect = useSelector(getPulseState);
  const orgUnitId = signedInOrgUnit?.id;

  const getExportStatusIconInHeader = useCallback((shouldShowIndicator, activeExportJobs, loadingState) => {
    // if showIndicator equals to true means the user started exporting just now
    // so there must have an InProgress job
    if (shouldShowIndicator) {
      return <DownloadInProgressIcon />;
    }

    // During loading, return the stored current icon
    if (loadingState) {
      return currentIcon;
    }

    // if there are any InProgress job, show the Download InProgress Icon
    const hasInProgressJobs = activeExportJobs.some(job => isJobStatusInProgress(job.jobStatus));
    if (hasInProgressJobs) {
      return <DownloadInProgressIcon />;
    }

    // show the Download Complete Icon as default
    return <DownloadCompleteIcon />;
  }, [currentIcon]);

  const getExportStatusIconInDropDown = exportJob => {
    if (isJobStatusInProgress(exportJob.jobStatus)
      || isJobStatusCancelled(exportJob.jobStatus)) {
      return <DownloadInProgressIcon />;
    }

    return <DownloadCompleteIcon />;
  };

  const hasExportJobs = useMemo(() => exportJobs.length > 0, [exportJobs]);

  const handleClick = useCallback(async target => {
    // Store the current icon state before making the API call
    setCurrentIcon(getExportStatusIconInHeader(showIndicator, exportJobs, false));

    dispatch(getExportJobList(orgUnitId))
      .then(response => {
        if (response?.payload?.exportJobs.length > 0) {
          setAnchorEl(target);
        }
      })
      .finally(() => {
        // remove the indicator only after the export job list is loaded
        // otherwise, there will be a timeframe where the export list is empty while loading,
        // but the indicator is removed. This will cause the hook useShouldShowReportExportDropDownButton to
        // think the export job list is empty and return false to hide the export button
        dispatch(removeExportCompleteIndicator());
      });
  }, [dispatch, orgUnitId, exportJobs, showIndicator, getExportStatusIconInHeader]);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDownload = useCallback(exportId => {
    downloadExportedReport(orgUnitId, exportId);
  }, [orgUnitId]);

  const handleClear = useCallback(exportId => {
    dispatch(clearExportedReport(orgUnitId, exportId));
  }, [dispatch, orgUnitId]);

  useEffect(() => {
    if (!exportCompleteJobId) {
      return;
    }

    // If the export was initiated in the current session, trigger the download
    if (wasExportInitiatedInCurrentSession) {
      const button = document.getElementById(BUTTON_ID);

      handleClick(button);
      handleDownload(exportCompleteJobId);
    }

    dispatch(removePendingExport(exportCompleteJobId));
    dispatch(clearExportCompleteJob());
  }, [dispatch, exportCompleteJobId, handleDownload, handleClick, wasExportInitiatedInCurrentSession]);

  return (
    <div className={classes.root}>
      <PulsingIconButton
        tooltip="View Exported Reports"
        id={BUTTON_ID}
        onClick={event => handleClick(event.currentTarget)}
        triggerPulse={showPulseEffect}
      >
        {getExportStatusIconInHeader(showIndicator, exportJobs, isLoading)}
      </PulsingIconButton>
      {hasExportJobs && (
        <Menu
          anchorEl={anchorEl}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={handleClose}
          // set the bottom-right corner of the IconButton for the popup menu to attach to
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          // set the popup menu to use its top-right corner to attach to the IconButton
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          // ensures the popup menu doesn't attach to the children element (CloudDownloadOutlineIcon) of the IconButton
          getContentAnchorEl={null}
        >
          {exportJobs.map(exportJob => {
            const isFailedOrCancelled = isJobStatusFailedOrCancelled(exportJob.jobStatus);

            return (
              <MenuItem
                key={exportJob.id}
                className={classes.menuItem}
                disableRipple
              >
                <div className={classes.menuItemContent}>
                  <Typography
                    variant="body2"
                    className={isFailedOrCancelled ? classes.fileNameFailed : classes.fileName}
                  >
                    {exportJob.exportFileName}
                  </Typography>
                  <Typography
                    variant="caption"
                    className={isFailedOrCancelled ? classes.statusLabelFailed : classes.statusLabel}
                  >
                    {getJobStatusLabel(exportJob.jobStatus)}
                  </Typography>
                </div>
                <div className={isFailedOrCancelled ? classes.actionButtonsFailed : classes.actionButtons}>
                  <IconButton
                    size="small"
                    disabled={!isJobStatusComplete(exportJob.jobStatus)}
                    className="download-button"
                    onClick={() => handleDownload(exportJob.id)}
                  >
                    {getExportStatusIconInDropDown(exportJob)}
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleClear(exportJob.id)}
                    className={isFailedOrCancelled ? classes.deleteButtonFailed : classes.deleteButton}
                  >
                    <DeleteIcon />
                  </IconButton>
                </div>
              </MenuItem>
            );
          })}
        </Menu>
      )}
    </div>
  );
};

export default ReportExportDropDown;
